// askSmart-ui/src/pages/TOSP/portal/useReveal.ts
import { useEffect, useState } from "react";

// A 0 → 1 progression over the given duration, eased out, run once on mount.
//
// Everything that should animate together multiplies by the same value, so the
// bar filling, the percentage climbing and the sales figure counting up stay in
// step by construction rather than by three timings being kept in sync.
//
// Returns 1 immediately when the viewer has asked for reduced motion — a
// counting figure is exactly the kind of movement that setting is for.
/**
 * Reduced motion gets the same 2.25s. A figure counting up in place is mild
 * enough that shortening it bought nothing except two timings to keep in step
 * — kept as its own constant so a genuine difference can be set later.
 */
const REDUCED_MOTION_MS = 2250;

export function useReveal(durationMs = 2250): number {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Reduced, not removed. The guidance exists for motion that can make
    // people unwell — parallax, spinning, things flying across the screen — and
    // a figure counting up in place isn't that. Cutting it short honours the
    // preference without leaving the tiles looking inert.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const duration = reduced ? REDUCED_MOTION_MS : durationMs;

    let frame = 0;
    let startedAt = 0;

    const step = (now: number) => {
      if (!startedAt) startedAt = now;
      const elapsed = (now - startedAt) / duration;
      if (elapsed >= 1) {
        setProgress(1);
        return;
      }
      // Ease-out cubic: quick off the mark, settling onto the final figure
      // rather than stopping dead on it.
      setProgress(1 - Math.pow(1 - elapsed, 3));
      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [durationMs]);

  return progress;
}
